import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/atoms/input";
import { Search, ChevronDown, X } from "lucide-react";

export interface SearchableOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value?: string | number | (string | number)[];
  onValueChange: (value: string | number | (string | number)[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  allOptionLabel?: string;
  showAllOption?: boolean;
  className?: string;
  width?: string;
  noResultsText?: string;
  searchPromptText?: string;
  multiple?: boolean;
  maxSelectedDisplay?: number;
}

export default function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  allOptionLabel = "Todos",
  showAllOption = true,
  className = "",
  width = "w-full sm:w-48",
  noResultsText = "No se encontraron resultados",
  searchPromptText = "Escribe para buscar...",
  multiple = false,
  maxSelectedDisplay = 2,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get current selected values as array
  const getSelectedValues = (): (string | number)[] => {
    if (multiple) {
      return Array.isArray(value)
        ? value.filter((v) => typeof v === "string" || typeof v === "number")
        : [];
    }
    return value !== undefined &&
      value !== "all" &&
      value !== "" &&
      !Array.isArray(value)
      ? [value]
      : [];
  };

  // Check if a value is selected
  const isSelected = (optionValue: string | number): boolean => {
    const selectedValues = getSelectedValues();
    return selectedValues.some((v) => v.toString() === optionValue.toString());
  };

  // Get display value for the trigger
  const getDisplayValue = () => {
    if (multiple) {
      const selectedValues = getSelectedValues();

      if (selectedValues.length === 0) {
        return placeholder;
      }

      if (selectedValues.length === 1) {
        const selectedOption = options.find(
          (option) => option.value.toString() === selectedValues[0].toString(),
        );
        return selectedOption?.label || placeholder;
      }

      if (selectedValues.length <= maxSelectedDisplay) {
        const selectedLabels = selectedValues
          .map(
            (val) =>
              options.find((opt) => opt.value.toString() === val.toString())
                ?.label,
          )
          .filter(Boolean);
        return selectedLabels.join(", ");
      }

      return `${selectedValues.length} seleccionados`;
    } else {
      if (value === "all" || value === "" || value === undefined) {
        return allOptionLabel;
      }

      const selectedOption = options.find(
        (option) => option.value.toString() === value.toString(),
      );
      return selectedOption?.label || placeholder;
    }
  };

  // Handle option selection
  const handleSelect = (optionValue: string | number) => {
    if (multiple) {
      const selectedValues = getSelectedValues();

      if (optionValue === "all") {
        // Toggle all selection
        if (selectedValues.length === options.length) {
          onValueChange([]);
        } else {
          onValueChange(options.map((opt) => opt.value));
        }
      } else {
        // Toggle individual selection
        if (isSelected(optionValue)) {
          onValueChange(
            selectedValues.filter(
              (val) => val.toString() !== optionValue.toString(),
            ),
          );
        } else {
          onValueChange([...selectedValues, optionValue]);
        }
      }
      // Don't close dropdown in multiple mode
    } else {
      onValueChange(optionValue);
      setSearchTerm("");
      setIsOpen(false);
    }
  };

  // Remove a selected item (for multiple mode)
  const removeSelectedItem = (
    valueToRemove: string | number,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (multiple) {
      const selectedValues = getSelectedValues();
      onValueChange(
        selectedValues.filter(
          (val) => val.toString() !== valueToRemove.toString(),
        ),
      );
    }
  };

  // Handle search input
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setIsOpen(true);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex h-10 ${width} items-center justify-between whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm transition-colors hover:bg-gray-50`}
      >
        <span className="text-gray-700 truncate">{getDisplayValue()}</span>
        <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
      </button>

      {/* Selected Items Chips (for multiple mode) */}
      {multiple && getSelectedValues().length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {getSelectedValues()
            .slice(0, maxSelectedDisplay)
            .map((selectedValue) => {
              const option = options.find(
                (opt) => opt.value.toString() === selectedValue.toString(),
              );
              return option ? (
                <div
                  key={selectedValue}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                >
                  <span>{option.label}</span>
                  <button
                    type="button"
                    onClick={(e) => removeSelectedItem(selectedValue, e)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                    aria-label={`Remover ${option.label}`}
                    title={`Remover ${option.label}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : null;
            })}
          {getSelectedValues().length > maxSelectedDisplay && (
            <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              +{getSelectedValues().length - maxSelectedDisplay} más
            </div>
          )}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 max-h-[300px] min-w-[200px] overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 shadow-lg mt-1">
          {/* Search Input */}
          <div className="p-2 pb-0">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-[200px] overflow-y-auto px-2 pb-2">
            {/* All Option */}
            {showAllOption && (
              <div
                onClick={() => handleSelect("all")}
                className={`relative flex w-full select-none items-center rounded-md py-2 pl-3 pr-3 text-sm cursor-pointer hover:bg-blue-50 ${
                  multiple
                    ? getSelectedValues().length === options.length
                      ? "bg-blue-50 text-blue-900"
                      : ""
                    : value === "all" || value === "" || value === undefined
                      ? "bg-blue-50 text-blue-900"
                      : ""
                }`}
              >
                {allOptionLabel}
              </div>
            )}

            {/* Filtered Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`relative flex w-full select-none items-center rounded-md py-2 pl-3 pr-9 text-sm cursor-pointer hover:bg-blue-50 ${
                    isSelected(option.value) ? "bg-blue-50 text-blue-900" : ""
                  }`}
                >
                  {option.label}
                  {multiple && isSelected(option.value) && (
                    <span className="absolute right-3 flex h-4 w-4 items-center justify-center">
                      <X className="h-3 w-3 text-blue-600" />
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? noResultsText : searchPromptText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
